/**
 * PDF Store (Zustand)
 *
 * Manages global state for PDF viewing and navigation:
 * - Current PDF being viewed
 * - Extraction results
 * - Navigation state (current index, total count)
 */

import { create } from 'zustand';
import type { PDFMetadata, ExtractedFieldData } from '../types/api';

interface PDFStore {
  // Current PDF
  currentPDFIndex: number;
  totalPDFs: number;
  currentPDF: PDFMetadata | null;
  extractionResults: Record<string, ExtractedFieldData>;
  pdfUrl: string | null;

  // PDF source highlighting
  highlightBbox: [number, number, number, number] | null;

  // Set current PDF with extraction results
  setCurrentPDF: (
    pdf: PDFMetadata,
    extractionResults: Record<string, ExtractedFieldData>,
    pdfUrl: string | null
  ) => void;

  // Navigate to PDF by index
  navigateTo: (index: number) => void;

  // Set total PDF count
  setTotalPDFs: (count: number) => void;

  // Set highlight bounding box
  setHighlightBbox: (bbox: [number, number, number, number] | null) => void;

  // Update only the PDF URL (for SAS refreshes)
  updatePDFUrl: (pdfUrl: string) => void;

  // Patch individual fields on currentPDF (mirrors edits saved from DataFieldsPanel)
  updateCurrentPDFFields: (fields: Partial<PDFMetadata>) => void;

  // Clear PDF state
  clearPDF: () => void;

  // Tebra Workflow State
  tebraWorkflowActive: boolean;
  setTebraWorkflowActive: (active: boolean) => void;
}

export const usePDFStore = create<PDFStore>((set) => ({
  currentPDFIndex: 0,
  totalPDFs: 0,
  currentPDF: null,
  extractionResults: {},
  pdfUrl: null,
  highlightBbox: null,
  tebraWorkflowActive: false,

  setCurrentPDF: (pdf, extractionResults, pdfUrl) =>
    set((state) => {
      // Revoke previous blob URL to prevent memory leaks
      if (state.pdfUrl) {
        URL.revokeObjectURL(state.pdfUrl);
      }
      return {
        currentPDF: pdf,
        extractionResults,
        pdfUrl,
        highlightBbox: null,
      };
    }),

  setTebraWorkflowActive: (active) => set({ tebraWorkflowActive: active }),

  navigateTo: (index) => set({ currentPDFIndex: index }),

  setTotalPDFs: (count) => set({ totalPDFs: count }),

  setHighlightBbox: (bbox) => set({ highlightBbox: bbox }),

  updatePDFUrl: (pdfUrl) => set({ pdfUrl }),

  updateCurrentPDFFields: (fields) =>
    set((state) => ({
      currentPDF: state.currentPDF ? { ...state.currentPDF, ...fields } : state.currentPDF,
    })),

  clearPDF: () =>
    set((state) => {
      if (state.pdfUrl) {
        URL.revokeObjectURL(state.pdfUrl);
      }
      return {
        currentPDFIndex: 0,
        totalPDFs: 0,
        currentPDF: null,
        extractionResults: {},
        pdfUrl: null,
        highlightBbox: null,
      };
    }),
}));
